import {filter, map, reduceRight, some} from 'lodash';
import moment from 'moment';
import {useContext, useEffect, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {LineType} from 'recharts';

import {accountApi} from 'api';
import {Card, Chart, DataWrapper} from 'components';
import {themeConfig} from 'constants/theme';
import {UserContext} from 'context';
import {useApiData} from 'hooks';
import {TRANSLATION_KEYS} from 'locale';
import {formatDate} from 'utils/transform';

const T_KEYS = TRANSLATION_KEYS.USER.ACCOUNT.BILLING.MAX_USERS_CHART;

enum DataKey {
    Date = 'date',
    MaxUsers = 'maxUsers'
}

export const MaxUsersChart: React.FC = () => {
    const {t} = useTranslation();
    const {settings: {common: {timeZone, dateFormat}}} = useContext(UserContext);

    const startDate = useMemo(() => moment().subtract(3, 'month'), []);
    const {send: getAccountPlan, loading, data: rawData, error} = useApiData(accountApi.getAccountPlan);

    const maxUsersHistory = useMemo(() => {
        const visibleHistoryItems = filter(rawData?.history, (historyItem) => moment(historyItem.period.from).isAfter(startDate));
        return visibleHistoryItems?.length ? [
            ...map(visibleHistoryItems, (planDetails) => ({
                maxUsers: planDetails.limits?.maxUsers,
                date: formatDate(planDetails.period.from, timeZone, dateFormat)
            })),
            {
                maxUsers: rawData.current.limits?.maxUsers,
                date: formatDate(rawData.current.period.from, timeZone, dateFormat)
            }, {
                maxUsers: rawData.current.limits?.maxUsers,
                date: formatDate(new Date(), timeZone, dateFormat)
            }
        ] : [];
    }, [rawData, timeZone, startDate, dateFormat]);

    const graphData = useMemo(() => reduceRight(maxUsersHistory, (acc, history) => {
        const hasDate = some(acc, {date: history.date});
        if (!hasDate) {
            acc.unshift(history);
        }
        return acc;
    }, []), [maxUsersHistory]);

    const areas = useMemo(() => [{
        type: 'stepAfter' as LineType,
        stackId: '1',
        dot: {stroke: 'white', fill: themeConfig.colors.primary, strokeWidth: 1, radius: 10},
        activeDot: false,
        dataKey: DataKey.MaxUsers,
        fill: themeConfig.colors.primary,
        isAnimationActive: false,
        name: t(T_KEYS.TITLE)
    }], [t]);

    useEffect(() => {
        getAccountPlan();
    }, [getAccountPlan]);

    return (
        <Card className='tw-mt-4'>
            <div className='tw-flex tw-items-center tw-justify-between'>
                <div className='tw-ts-h3-bold -md:tw-ts-h5-bold tw-mb-4'>{t(T_KEYS.TITLE)}</div>
            </div>

            <div className='tw-h-80 tw-relative'>
                <DataWrapper isLoading={loading || !rawData} isError={!!error}>
                    <Chart
                        tooltip={{active: true}}
                        margin={{top: 20, right: 0, left: 0, bottom: 5}}
                        grid={{stroke: themeConfig.colors.lightGray}}
                        xAxis={{dataKey: DataKey.Date}}
                        yAxis={{dataKey: DataKey.MaxUsers}}
                        areas={areas}
                        data={graphData}
                    />
                </DataWrapper>
            </div>
        </Card>
    );
};